import './App.less';

import { Auth0Provider } from '@auth0/auth0-react';
import { theme as metTheme } from '@met/react-components';
import { notification } from 'antd';
import theme from './styled-config';
import '@met/react-components/dist/polyfill';
import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import 'react-app-polyfill/ie11';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Route } from 'react-router';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from 'styled-components';
import { QueryParamProvider } from 'use-query-params';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './helpers/appInsights';

import App from './App';
import { BaseProvider } from './components/Layout';
import BaseLayout from './components/Layout/BaseLayout/BaseLayout';
import ServerError from './components/PageError/types/ServerError';
import './config/fontAwesome';
import { getAiInstrumentationKey, setAiInstrumentationKey } from './helpers/sessionStorage';
import history from './history';
import IntlProvider from './locales/IntlProvider';
import { unregister } from './registerServiceWorker';
import configureStore from './store/configureStore';
import googleTagManager from './helpers/googleTagManager';

// configure react toastify
toast.configure({
  autoClose: 8000,
  draggable: false,
  position: toast.POSITION.BOTTOM_RIGHT,
});

// Configure Antd notifications
notification.config({
  placement: 'bottomRight',
});

// Get the application-wide store instance, prepopulating with state from the server where available.
const initialState = window.initialReduxState;
const store = configureStore(history, initialState);

const rootElement = document.getElementById('root');

// Auth0 redirect that routes user to right place after login
const onRedirectCallback = appState => {
  let redirect = appState?.returnTo || window.location.pathname;

  history.replace(`/login/?r=${redirect}`);
};

const getConfiguration = () => {
  return fetch('/api/v1/configuration').then(response => {
    return response.json();
  });
};

history.listen((location, action) => {
  googleTagManager.pageView(`${location.pathname}${location.search}`);
  window.scrollTo(0, 0);
});

getConfiguration()
  .then(config => {
    const { auth0, appInsights } = config.data;

    // Configure client-side AppInsights
    if (appInsights.instrumentationKey && getAiInstrumentationKey() === '') {
      setAiInstrumentationKey(appInsights.instrumentationKey);
    }

    let completeTheme = { ...theme, ...metTheme };

    ReactDOM.render(
      <Provider store={store}>
        <IntlProvider>
          <Auth0Provider
            domain={auth0.domain}
            clientId={auth0.clientId}
            redirectUri={window.location.origin}
            onRedirectCallback={onRedirectCallback}
            audience={auth0.audience}
            scope='openid profile email connect'
            cacheLocation='localstorage'
          >
            <BaseProvider>
              <ConnectedRouter history={history}>
                <ThemeProvider theme={completeTheme}>
                  <QueryParamProvider ReactRouterRoute={Route}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                      <App />
                    </AppInsightsContext.Provider>
                  </QueryParamProvider>
                </ThemeProvider>
              </ConnectedRouter>
            </BaseProvider>
          </Auth0Provider>
        </IntlProvider>
      </Provider>,
      rootElement
    );
  })
  .catch(() => {
    ReactDOM.render(
      <Provider store={store}>
        <IntlProvider>
          <Auth0Provider
            domain=''
            clientId=''
            redirectUri={window.location.origin}
            onRedirectCallback={onRedirectCallback}
            audience=''
            scope='openid profile email connect'
            cacheLocation='localstorage'
          >
            <BaseProvider>
              <ConnectedRouter history={history}>
                <ThemeProvider theme={theme}>
                  <QueryParamProvider ReactRouterRoute={Route}>
                    <BaseLayout>
                      <ServerError />
                    </BaseLayout>
                  </QueryParamProvider>
                </ThemeProvider>
              </ConnectedRouter>
            </BaseProvider>
          </Auth0Provider>
        </IntlProvider>
      </Provider>,
      rootElement
    );
  });

unregister();
